import React, { useEffect, useState } from 'react';
import { Button, Drawer, Form, Radio, Space, message } from 'antd';
import { TruckOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { apiRequests } from 'src/shared/api/api';

import RegularDeliveryForm from './DocSalesOrderDrawer/RegularDeliveryForm';
import CdekOrderForm from './DocSalesOrderDrawer/CdekOrderForm';
import useCdekOrder from './DocSalesOrderDrawer/useCdekOrder';

const DocSalesOrderDrawer = ({
  deliveryInfo,
  docId,
  onSave,
  isNew = false,
  items = [],
  warehouse,
  token,
  onCdekOrderCreated,
  recipient = null,
  onCdekOrderDeleted,
}) => {
  const [visible, setVisible]               = useState(false);
  const [mode, setMode]                     = useState('regular');
  const [form]                              = Form.useForm();
  const [loadingDelivery, setLoadingDelivery] = useState(false);

  const cdek = useCdekOrder({
    docId, token, form, items, warehouse, recipient, onCdekOrderCreated, onCdekOrderDeleted,
  });

  // ── Загрузить заказ СДЭК при открытии в режиме cdek ──────────────────────────
  useEffect(() => {
    if (visible && mode === 'cdek' && docId) {
      cdek.loadCdekOrderByDoc();
    }
  }, [visible, mode, docId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Prefill при открытии в режиме cdek ───────────────────────────────────────
  useEffect(() => {
    if (visible && mode === 'cdek') {
      const t = setTimeout(cdek.updateFormWithItemsAndWarehouse, 50);
      return () => clearTimeout(t);
    }
  }, [visible, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── При изменении товаров — обновить пакеты в СДЭК форме ─────────────────────
  useEffect(() => {
    if (visible && mode === 'cdek') {
      cdek.updateFormWithItemsAndWarehouse();
    }
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Синхронизация данных обычной доставки → СДЭК ────────────────────────────
  useEffect(() => {
    if (visible && mode === 'cdek' && deliveryInfo) {
      cdek.syncFromRegularDelivery(deliveryInfo);
    }
  }, [visible, mode, deliveryInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Prefill формы обычной доставки ───────────────────────────────────────────
  useEffect(() => {
    if (visible && mode === 'regular' && deliveryInfo && form) {
      form.resetFields();
      const recipientFullName = deliveryInfo.recipient
        ? `${deliveryInfo.recipient.name || ''} ${deliveryInfo.recipient.surname || ''}`.trim()
        : '';
      form.setFieldsValue({
        ...deliveryInfo,
        delivery_date:      deliveryInfo.delivery_date ? dayjs(deliveryInfo.delivery_date * 1000) : null,
        recipient_fullname: recipientFullName,
        recipient_phone:    deliveryInfo.recipient?.phone || '',
        delivery_price:     deliveryInfo.delivery_price || undefined,
      });
    }
  }, [visible, mode, deliveryInfo, form]);

  // ── Переключение режима ───────────────────────────────────────────────────────
  const handleModeChange = (newMode) => {
    setMode(newMode);
    if (newMode === 'regular') cdek.resetCdekState();
  };

  // ── Сохранение обычной доставки ───────────────────────────────────────────────
  const handleSaveRegular = async () => {
    try {
      const values = await form.validateFields();
      const deliveryDate = values.delivery_date
        ? Math.floor(values.delivery_date.valueOf() / 1000) : null;
      const fullName  = values.recipient_fullname || '';
      const nameParts = fullName.trim().split(/\s+/);
      const payload   = {
        ...values,
        delivery_date: deliveryDate,
        recipient: { name: nameParts[0] || '', surname: nameParts.slice(1).join(' ') || '', phone: values.recipient_phone },
      };
      delete payload.recipient_fullname;
      delete payload.recipient_phone;

      setLoadingDelivery(true);
      try {
        const response = await apiRequests.docs.postSalesDeliveryInfo(docId, payload);
        message.success('Информация о доставке обновлена');
        // Синхронизируем сохранённые данные → СДЭК форму
        cdek.syncFromRegularDelivery({
          ...payload,
          recipient_fullname: values.recipient_fullname,
          recipient_phone:    values.recipient_phone,
        });
        if (onSave) onSave(response.data);
        setVisible(false);
      } catch (err) {
        console.error(err);
        message.error('Не удалось обновить информацию о доставке');
      } finally {
        setLoadingDelivery(false);
      }
    } catch (err) {
      console.error(err);
      if (err.errorFields?.length) message.error('Пожалуйста, проверьте введенные данные');
      else message.error('Ошибка при сохранении данных');
    }
  };

  // ── Копировать поле ───────────────────────────────────────────────────────────
  const handleCopyField = (fieldName) => {
    const value = form.getFieldValue(fieldName);
    if (!value) { message.warning('Нет данных для копирования'); return; }
    navigator.clipboard
      .writeText(value)
      .then(() => message.success('Скопировано'))
      .catch(() => message.error('Не удалось скопировать'));
  };

  return (
    <>
      <Button
        onClick={() => setVisible(true)}
        style={{ width: '100%', marginBottom: 12 }}
        icon={<TruckOutlined />}
      >
        Доставка
      </Button>

      <Drawer
        open={visible}
        onClose={() => setVisible(false)}
        title="Данные о доставке"
        placement="right"
        width={720}
        footer={
          mode === 'regular' ? (
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setVisible(false)}>Отмена</Button>
              <Button type="primary" onClick={handleSaveRegular} loading={loadingDelivery}>
                Сохранить
              </Button>
            </Space>
          ) : null
        }
      >
        <Radio.Group
          value={mode}
          onChange={(e) => handleModeChange(e.target.value)}
          style={{ marginBottom: 16 }}
          block
        >
          <Radio.Button value="regular">Обычная доставка</Radio.Button>
          <Radio.Button value="cdek">СДЭК</Radio.Button>
        </Radio.Group>

        <Form form={form} layout="vertical" key={mode}>
          {mode === 'regular' ? (
            <RegularDeliveryForm onCopyField={handleCopyField} />
          ) : (
            <CdekOrderForm form={form} {...cdek} />
          )}
        </Form>
      </Drawer>
    </>
  );
};

export default DocSalesOrderDrawer;